import { rememberTenant } from "./tenant-continuity.js";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import "./crm-workspace.css";
import "./crm-pipeline.css";
import { PreparationP1Shell } from "./preparation-p1-shell.js";
import {
  CommercialBrief,
  TemperaturePill,
  type CommercialInsight,
} from "./commercial-insight.js";
import { ChannelOperationalStatus, OperationalBell } from "./operational-indicators.js";

type Stage = string;
type PipelineStage = {
  id: string;
  stage_key: string;
  label: string;
  position: number;
  is_terminal: boolean;
  terminal_outcome: "won" | "lost" | null;
  is_active: boolean;
  accent?: string;
};
type Lead = {
  id: string;
  conversation_id: string;
  stage: Stage;
  stage_id: string;
  stage_key: string;
  stage_label: string;
  stage_position: number;
  stage_terminal: boolean;
  terminal_outcome: "won" | "lost" | null;
  revision: number;
  last_change_source: "human" | "system" | "automation";
  override_dimensions: string[];
  assigned_user_id: string | null;
  assignee_name: string | null;
  tags: string[];
  internal_notes: string;
  next_action: string;
  next_action_at: string | null;
  value_cents: number | null;
  created_at: string;
  updated_at: string;
  display_name: string | null;
  external_id: string;
  ai_mode: string;
  last_text: string | null;
  last_at: string | null;
  last_content_type: string | null;
  avatar_url: string | null;
  commercial_temperature: CommercialInsight["temperature"];
  commercial_reason: string | null;
  commercial_updated_at: string | null;
  commercial_source: CommercialInsight["source"];
};
type LeadEvent = {
  id: string;
  operation: string;
  source: "human" | "automation" | "system";
  metadata: Record<string, unknown>;
  created_at: string;
  actor_name: string | null;
};
type Operator = { id: string; display_name: string };
type Overview = {
  period: string;
  new_leads: number;
  active: number;
  waiting: number;
  negotiation: number;
  won: number;
  lost: number;
  due: number;
  inbound: number;
  ai: number;
  human: number;
};
type Subscription = {
  planName: string;
  status: string;
  usage: { used: number; limit: number; remaining: number } | null;
} | null;
type Channel = { status: string; ai_enabled?:boolean } | null;
type GoogleAccount = { configured: boolean; linked: boolean };
type PreparationContent = {
  companyName: string;
  businessArea: string;
  services: string[];
  commercialInfo: string;
  businessHours: string;
  businessObjectives: string;
  humanHandoff: string;
  faqs: Array<{ question: string; answer: string }>;
  aiInstructions: string;
};

// The Core intentionally returns an empty active payload for a fresh C2 QA
// run. Normalize at the Web boundary so dashboard-only derived state never
// assumes a saved business profile exists before the owner starts testing.
function preparationContent(value?: Partial<PreparationContent>): PreparationContent {
  return {
    companyName: value?.companyName ?? "",
    businessArea: value?.businessArea ?? "",
    services: [...new Set([...(Array.isArray(value?.services) ? value.services : []),...((value as Partial<PreparationContent> & {knowledge?:{offers?:Array<{name:string;reviewed:boolean}>}})?.knowledge?.offers??[]).filter(o=>o.reviewed).map(o=>o.name)].map(name=>name.trim()).filter(Boolean))],
    commercialInfo: value?.commercialInfo ?? "",
    businessHours: value?.businessHours ?? "",
    businessObjectives: value?.businessObjectives ?? "",
    humanHandoff: value?.humanHandoff ?? "",
    faqs: Array.isArray(value?.faqs) ? value.faqs : [],
    aiInstructions: value?.aiInstructions ?? "",
  };
}
type PreparationState = {
  onboarding: "draft" | "ready" | "legacy";
  pending: { companyName?: string; businessArea?: string } | null;
  activeVersion: number;
  active: PreparationContent;
  hasDraft: boolean;
  /** A dedicated C2 QA run deliberately starts with no business facts. */
  qaClean: boolean;
};
const pipelineAccents = [
  "#4f8ef3",
  "#22a6a0",
  "#e5a84b",
  "#8d77df",
  "#42a884",
  "#a7b0c2",
  "#d577a3",
  "#3e9bc0",
];
const defaultStages: PipelineStage[] = [
  {
    id: "new",
    stage_key: "new",
    label: "Novo",
    position: 10,
    is_terminal: false,
    terminal_outcome: null,
    is_active: true,
  },
  {
    id: "active",
    stage_key: "active",
    label: "Em atendimento",
    position: 20,
    is_terminal: false,
    terminal_outcome: null,
    is_active: true,
  },
  {
    id: "waiting",
    stage_key: "waiting",
    label: "Aguardando cliente",
    position: 30,
    is_terminal: false,
    terminal_outcome: null,
    is_active: true,
  },
  {
    id: "negotiation",
    stage_key: "negotiation",
    label: "Negociação",
    position: 40,
    is_terminal: false,
    terminal_outcome: null,
    is_active: true,
  },
  {
    id: "won",
    stage_key: "won",
    label: "Ganho",
    position: 50,
    is_terminal: true,
    terminal_outcome: "won",
    is_active: true,
  },
  {
    id: "lost",
    stage_key: "lost",
    label: "Perdido",
    position: 60,
    is_terminal: true,
    terminal_outcome: "lost",
    is_active: true,
  },
];
const date = (value: string | null) =>
  value
    ? new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(value))
    : "Sem interação";
const localInputDate = (value: string | null) =>
  value
    ? new Date(value)
        .toLocaleString("sv-SE", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
        .replace(" ", "T")
    : "";
const money = (cents: number | null) =>
  cents === null
    ? ""
    : new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        maximumFractionDigits: 0,
      }).format(cents / 100);
const request = async <T,>(
  path: string,
  token: string,
  options: RequestInit = {},
): Promise<T> => {
  const response = await fetch(`/api${path}`, {
    ...options,
    headers: {
      authorization: `Bearer ${token}`,
      ...(options.body ? { "content-type": "application/json" } : {}),
      ...options.headers,
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new Error(
      (data as { message?: string }).message ??
        "Não foi possível carregar os dados.",
    );
  return data as T;
};

type PipelineIconName =
  | "opportunity"
  | "value"
  | "calendar"
  | "alert"
  | "search"
  | "edit"
  | "message"
  | "chevronLeft"
  | "chevronRight"
  | "action"
  | "owner"
  | "lead"
  | "active"
  | "waiting"
  | "due";
function PipelineIcon({ name }: { name: PipelineIconName }) {
  const shape: Record<PipelineIconName, React.ReactNode> = {
    opportunity: (
      <>
        <rect x="3" y="7" width="18" height="14" rx="2" />
        <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18" />
      </>
    ),
    value: (
      <>
        <path d="M3 17l6-6 4 4 8-8" />
        <path d="M15 7h6v6" />
      </>
    ),
    calendar: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M8 3v4M16 3v4M3 10h18" />
      </>
    ),
    alert: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v6M12 17h.01" />
      </>
    ),
    search: (
      <>
        <circle cx="10.8" cy="10.8" r="6.8" />
        <path d="m16 16 5 5" />
      </>
    ),
    edit: (
      <>
        <path d="M12 20h9M3 17.5V21h3.5L18.9 8.6a2.1 2.1 0 0 0-3-3L3 17.5Z" />
      </>
    ),
    message: (
      <path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5H5l-2 2v-8.5A8.5 8.5 0 1 1 21 11.5Z" />
    ),
    chevronLeft: <path d="m15 18-6-6 6-6" />,
    chevronRight: <path d="m9 18 6-6-6-6" />,
    action: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    owner: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0" />
      </>
    ),
    lead: (
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3.5 20a5.5 5.5 0 0 1 11 0M16 7h5M18.5 4.5v5" />
      </>
    ),
    active: (
      <>
        <path d="M4 20V10l8-6 8 6v10" />
        <path d="M9 20v-6h6v6M8 10h.01M16 10h.01" />
      </>
    ),
    waiting: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    due: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5M12 17h.01" />
      </>
    ),
  };
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {shape[name]}
    </svg>
  );
}

export function CrmWorkspace({
  token,
  tenantId,
  tenantName,
  tenantRole,
  view,
  subscription,
  channel,
  focusConversationId,
  onFocusConsumed,
  onOpenInbox,
  onOpenPreparation,
  onTenantNameChange,
}: {
  token: string;
  tenantId: string;
  tenantName: string;
  tenantRole?: string;
  view: "dashboard" | "pipeline" | "settings" | "preparation";
  tenants?: { id: string; name: string }[];
  onTenantChange?: (id: string) => void;
  subscription: Subscription;
  channel: Channel;
  focusConversationId: string;
  onFocusConsumed: () => void;
  onOpenInbox: (conversationId: string) => void;
  onOpenPreparation: () => void;
  onTenantNameChange?: (name: string) => void;
}) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [period, setPeriod] = useState<"7d" | "30d" | "90d">("30d");
  const [query, setQuery] = useState("");
  const [stageFilter, setStageFilter] = useState<Stage | "all">("all");
  const [pipelineStages, setPipelineStages] = useState<PipelineStage[]>([]);
  const [stageSettingsOpen, setStageSettingsOpen] = useState(false);
  const [stageDraft, setStageDraft] = useState<PipelineStage[]>([]);
  const [stageSettingsBusy, setStageSettingsBusy] = useState(false);
  const [sort, setSort] = useState<"recent" | "action" | "value">("recent");
  const [editing, setEditing] = useState<Lead | null>(null);
  const [valueInput, setValueInput] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [commercialInsight, setCommercialInsight] =
    useState<CommercialInsight | null>(null);
  const [leadEvents, setLeadEvents] = useState<LeadEvent[]>([]);
  const [insightLoading, setInsightLoading] = useState(false);
  const boardRef = useRef<HTMLDivElement>(null);
  const [boardIndex, setBoardIndex] = useState(0);
  const [boardEdges, setBoardEdges] = useState({ start: true, end: false });
  const [googleAccount, setGoogleAccount] = useState<GoogleAccount | null>(
    null,
  );
  const [googleLinkBusy, setGoogleLinkBusy] = useState(false);
  const [preparationState, setPreparationState] =
    useState<PreparationState | null>(null);
  const [preparationEpoch, setPreparationEpoch] = useState(0);

  useEffect(() => {
    if (!tenantId) return;
    let live = true;
    setLoading(true);
    setError("");
    setLeads([]);
    setOverview(null);
    setEditing(null);
    const load = async () => {
      try {
        const [
          leadResponse,
          operatorResponse,
          overviewResponse,
          stageResponse,
        ] = await Promise.all([
          request<{ data: Lead[] }>(`/v1/tenants/${tenantId}/crm/leads`, token),
          request<{ data: Operator[] }>(
            `/v1/tenants/${tenantId}/crm/operators`,
            token,
          ),
          request<{ data: Overview }>(
            `/v1/tenants/${tenantId}/crm/overview?period=${period}`,
            token,
          ),
          request<{ data: PipelineStage[] }>(
            `/v1/tenants/${tenantId}/crm/pipeline/stages`,
            token,
          ),
        ]);
        if (live) {
          setLeads(leadResponse.data);
          setOperators(operatorResponse.data);
          setOverview(overviewResponse.data);
          setPipelineStages(stageResponse.data);
          setLoading(false);
        }
      } catch (cause) {
        if (live) {
          setError((cause as Error).message);
          setLoading(false);
        }
      }
    };
    void load();
    const timer = window.setInterval(load, 15000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [token, tenantId, period]);

  useEffect(() => {
    if (view !== "settings") return;
    let live = true;
    void request<{ data: GoogleAccount }>("/v1/account/google", token)
      .then((result) => live && setGoogleAccount(result.data))
      .catch((cause: Error) => live && setError(cause.message));
    return () => {
      live = false;
    };
  }, [token, view]);

  useEffect(() => {
    if (!tenantId || tenantRole !== "owner") {
      setPreparationState(null);
      return;
    }
    let live = true;
    void Promise.all([
      request<{
        data: {
          status: "draft" | "ready" | "legacy";
          answers?: { companyName?: string; businessArea?: string };
          qa?: { clean?: boolean };
        };
      }>(`/v1/tenants/${tenantId}/onboarding/experience`, token),
      request<{
        data: {
          active: { version: number; content: Partial<PreparationContent> };
          draft: unknown | null;
          qa?: { clean?: boolean };
        };
      }>(`/v1/tenants/${tenantId}/preparation`, token),
    ])
      .then(([onboarding, preparation]) => {
        if (live)
          setPreparationState({
            onboarding: onboarding.data.status,
            pending:
              onboarding.data.status === "draft"
                ? (onboarding.data.answers ?? null)
                : null,
            activeVersion: preparation.data.active.version,
            active: preparationContent(preparation.data.active.content),
            hasDraft: Boolean(preparation.data.draft),
            qaClean:
              onboarding.data.qa?.clean === true &&
              preparation.data.qa?.clean === true,
          });
      })
      .catch(() => live && setPreparationState(null));
    return () => {
      live = false;
    };
  }, [token, tenantId, tenantRole, preparationEpoch]);

  async function linkGoogle() {
    setGoogleLinkBusy(true);
    setError("");
    try {
      const result = await request<{ data: { authorizationUrl: string } }>(
        "/v1/account/google/link/start",
        token,
        {
          method: "POST",
          body: JSON.stringify({ returnTo: "/app?view=settings" }),
        },
      );
      window.location.assign(result.data.authorizationUrl);
    } catch (cause) {
      setError((cause as Error).message);
      setGoogleLinkBusy(false);
    }
  }

  const displayStages = pipelineStages.length
    ? pipelineStages.filter((stage) => stage.is_active || stage.is_terminal)
    : defaultStages;
  const stageAccent = (stage: PipelineStage) =>
    stage.accent ??
    pipelineAccents[
      Math.max(
        0,
        displayStages.findIndex((item) => item.id === stage.id),
      ) % pipelineAccents.length
    ]!;
  const filtered = useMemo(() => {
    const needle = query.toLocaleLowerCase("pt-BR").trim();
    return leads
      .filter(
        (lead) =>
          (stageFilter === "all" || lead.stage === stageFilter) &&
          (!needle ||
            `${lead.display_name ?? ""} ${lead.external_id} ${lead.last_text ?? ""} ${lead.tags.join(" ")}`
              .toLocaleLowerCase("pt-BR")
              .includes(needle)),
      )
      .sort((a, b) =>
        sort === "value"
          ? (b.value_cents ?? -1) - (a.value_cents ?? -1)
          : sort === "action"
            ? (a.next_action_at ?? "9999").localeCompare(
                b.next_action_at ?? "9999",
              )
            : new Date(b.last_at ?? b.updated_at).getTime() -
              new Date(a.last_at ?? a.updated_at).getTime(),
      );
  }, [leads, query, stageFilter, sort]);
  const visibleStages = displayStages.filter(
    (item) => stageFilter === "all" || stageFilter === item.stage_key,
  );
  const openLeads = leads.filter(
    (lead) => lead.stage !== "won" && lead.stage !== "lost",
  );
  const pricedOpenLeads = openLeads.filter((lead) => lead.value_cents !== null);
  const openValue = pricedOpenLeads.reduce(
    (total, lead) => total + (lead.value_cents ?? 0),
    0,
  );
  const now = Date.now();
  const upcomingActions = openLeads.filter(
    (lead) =>
      lead.next_action_at && new Date(lead.next_action_at).getTime() > now,
  ).length;
  const overdueActions = openLeads.filter(
    (lead) =>
      lead.next_action_at && new Date(lead.next_action_at).getTime() <= now,
  ).length;
  function scrollStage(index: number) {
    const board = boardRef.current;
    const column = board?.querySelectorAll<HTMLElement>(".crm-column")[index];
    const first = board?.querySelector<HTMLElement>(".crm-column");
    if (!board || !column || !first) return;
    board.scrollTo({
      left: column.offsetLeft - first.offsetLeft,
      behavior: "smooth",
    });
    setBoardIndex(index);
  }
  function updateBoardIndex() {
    const board = boardRef.current;
    const first = board?.querySelector<HTMLElement>(".crm-column");
    if (!board || !first) return;
    const gap = Number.parseFloat(getComputedStyle(board).columnGap) || 0;
    setBoardIndex(
      Math.min(
        visibleStages.length - 1,
        Math.max(0, Math.round(board.scrollLeft / (first.offsetWidth + gap))),
      ),
    );
    setBoardEdges({
      start: board.scrollLeft <= 2,
      end: board.scrollLeft + board.clientWidth >= board.scrollWidth - 2,
    });
  }
  useEffect(() => {
    if (view !== "pipeline" || loading) return;
    const frame = requestAnimationFrame(updateBoardIndex);
    window.addEventListener("resize", updateBoardIndex);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", updateBoardIndex);
    };
  }, [view, loading, stageFilter, filtered.length]);

  const leadPayload = (
    lead: Lead,
    stageKey: Stage = lead.stage,
    confirmTerminal = false,
  ) => {
    const target = displayStages.find((stage) => stage.stage_key === stageKey);
    return {
      stageId: target?.id === stageKey ? undefined : target?.id,
      stage: target?.id === stageKey ? stageKey : undefined,
      assignedUserId: lead.assigned_user_id,
      tags: lead.tags,
      internalNotes: lead.internal_notes,
      nextAction: lead.next_action,
      nextActionAt: lead.next_action_at,
      valueCents: lead.value_cents,
      revision: lead.revision,
      confirmTerminal,
    };
  };
  async function refreshPipeline() {
    const [leadResponse, overviewResponse, stageResponse] = await Promise.all([
      request<{ data: Lead[] }>(`/v1/tenants/${tenantId}/crm/leads`, token),
      request<{ data: Overview }>(
        `/v1/tenants/${tenantId}/crm/overview?period=${period}`,
        token,
      ),
      request<{ data: PipelineStage[] }>(
        `/v1/tenants/${tenantId}/crm/pipeline/stages`,
        token,
      ),
    ]);
    setLeads(leadResponse.data);
    setOverview(overviewResponse.data);
    setPipelineStages(stageResponse.data);
  }
  async function save(
    lead: Lead,
    nextStage: Stage = lead.stage,
    confirmedTerminal = false,
  ) {
    setSaving(true);
    setError("");
    try {
      await request(`/v1/tenants/${tenantId}/crm/leads/${lead.id}`, token, {
        method: "PATCH",
        body: JSON.stringify(leadPayload(lead, nextStage, confirmedTerminal)),
      });
      await refreshPipeline();
      setEditing(null);
    } catch (cause) {
      const message = (cause as Error).message;
      setError(message);
      if (message.includes("foi alterada por outra pessoa"))
        void refreshPipeline();
    } finally {
      setSaving(false);
    }
  }
  function requestMove(lead: Lead, nextStage: Stage) {
    const target = displayStages.find((stage) => stage.stage_key === nextStage);
    if (
      target?.is_terminal &&
      lead.stage !== nextStage &&
      !window.confirm(
        `Confirmar que esta oportunidade deve ser marcada como “${target.label}”? Esta decisão será registrada como uma ação humana.`,
      )
    )
      return;
    void save(
      lead,
      nextStage,
      Boolean(target?.is_terminal && lead.stage !== nextStage),
    );
  }
  async function addStage() {
    setStageSettingsBusy(true);
    setError("");
    try {
      await request(`/v1/tenants/${tenantId}/crm/pipeline/stages`, token, {
        method: "POST",
        body: JSON.stringify({ label: "Nova etapa" }),
      });
      await refreshPipeline();
      const refreshed = await request<{ data: PipelineStage[] }>(
        `/v1/tenants/${tenantId}/crm/pipeline/stages`,
        token,
      );
      setStageDraft(refreshed.data);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setStageSettingsBusy(false);
    }
  }
  async function saveStages() {
    setStageSettingsBusy(true);
    setError("");
    try {
      const normalized = stageDraft.map((stage, index) => ({
        id: stage.id,
        label: stage.label.trim(),
        position: (index + 1) * 10,
        isActive: stage.is_active,
      }));
      await request(`/v1/tenants/${tenantId}/crm/pipeline/stages`, token, {
        method: "PUT",
        body: JSON.stringify({ stages: normalized }),
      });
      await refreshPipeline();
      setStageSettingsOpen(false);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setStageSettingsBusy(false);
    }
  }
  async function releaseHumanControl(
    lead: Lead,
    dimension: "stage" | "assignment" | "temperature",
  ) {
    setSaving(true);
    setError("");
    try {
      await request(
        `/v1/tenants/${tenantId}/crm/leads/${lead.id}/overrides/${dimension}/release`,
        token,
        { method: "POST", body: JSON.stringify({ revision: lead.revision }) },
      );
      await refreshPipeline();
      setEditing(null);
    } catch (cause) {
      const message = (cause as Error).message;
      setError(message);
      if (message.includes("foi alterada por outra pessoa"))
        void refreshPipeline();
    } finally {
      setSaving(false);
    }
  }
  function openEdit(lead: Lead) {
    setEditing({ ...lead });
    setValueInput(
      lead.value_cents === null
        ? ""
        : (lead.value_cents / 100).toFixed(2).replace(".", ","),
    );
    setTagsInput(lead.tags.join(", "));
    setCommercialInsight(null);
    setLeadEvents([]);
    setError("");
  }
  useEffect(() => {
    if (!editing) return;
    let live = true;
    setInsightLoading(true);
    void Promise.all([
      request<{ data: CommercialInsight | null }>(
        `/v1/tenants/${tenantId}/crm/leads/${editing.id}/insight`,
        token,
      ),
      request<{ data: LeadEvent[] }>(
        `/v1/tenants/${tenantId}/crm/leads/${editing.id}/events`,
        token,
      ),
    ])
      .then(([insight, events]) => {
        if (live) {
          setCommercialInsight(insight.data);
          setLeadEvents(events.data);
        }
      })
      .catch((cause: Error) => live && setError(cause.message))
      .finally(() => live && setInsightLoading(false));
    return () => {
      live = false;
    };
  }, [editing?.id, tenantId, token]);
  async function setTemperature(
    lead: Lead,
    temperature: "cold" | "warm" | "hot",
  ) {
    setSaving(true);
    setError("");
    try {
      await request(
        `/v1/tenants/${tenantId}/crm/leads/${lead.id}/temperature`,
        token,
        {
          method: "PATCH",
          body: JSON.stringify({ temperature, revision: lead.revision }),
        },
      );
      await refreshPipeline();
      setEditing(null);
    } catch (cause) {
      const message = (cause as Error).message;
      setError(message);
      if (message.includes("foi alterada por outra pessoa"))
        void refreshPipeline();
    } finally {
      setSaving(false);
    }
  }
  useEffect(() => {
    if (!focusConversationId || loading) return;
    const match = leads.find(
      (lead) => lead.conversation_id === focusConversationId,
    );
    if (match) openEdit(match);
    onFocusConsumed();
  }, [focusConversationId, leads, loading]);
  function submitEdit(event: FormEvent) {
    event.preventDefault();
    if (!editing) return;
    const normalized = valueInput
      .replace(/\s/g, "")
      .replace(/\./g, "")
      .replace(",", ".");
    const value = normalized === "" ? null : Number(normalized);
    if (
      value !== null &&
      (!Number.isFinite(value) || value < 0 || value > 10000000)
    ) {
      setError("Informe um valor válido em reais.");
      return;
    }
    const changed = {
      ...editing,
      value_cents: value === null ? null : Math.round(value * 100),
      tags: [
        ...new Set(
          tagsInput
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean),
        ),
      ],
    };
    const target = displayStages.find(
      (stage) => stage.stage_key === changed.stage,
    );
    if (
      target?.is_terminal &&
      changed.stage !== editing.stage &&
      !window.confirm(
        `Confirmar que esta oportunidade deve ser marcada como “${target.label}”? Esta decisão será registrada como uma ação humana.`,
      )
    )
      return;
    void save(
      changed,
      changed.stage,
      Boolean(target?.is_terminal && changed.stage !== editing.stage),
    );
  }
  function card(lead: Lead) {
    const name = lead.display_name || lead.external_id;
    const contactId = lead.external_id.replace(/@[^@]+$/, "");
    const actionDue =
      lead.next_action_at &&
      new Date(lead.next_action_at).getTime() <= Date.now();
    const channelLabel = lead.external_id.endsWith("@s.whatsapp.net")
      ? "WhatsApp"
      : null;
    return (
      <article
        key={lead.id}
        className="crm-card"
        draggable
        onDragStart={(event) =>
          event.dataTransfer.setData("text/plain", lead.id)
        }
      >
        <div className="crm-card-top">
          <span className={`crm-avatar ${lead.avatar_url ? "has-photo" : ""}`}>
            {lead.avatar_url ? (
              <img src={lead.avatar_url} alt="" />
            ) : (
              name.slice(0, 2).toUpperCase()
            )}
          </span>
          <div className="crm-card-person">
            <strong>{name}</strong>
            <small>{contactId}</small>
          </div>
          <button
            type="button"
            className="crm-card-more"
            aria-label={`Editar ${name}`}
            title="Editar oportunidade"
            onClick={() => openEdit(lead)}
          >
            <PipelineIcon name="edit" />
          </button>
        </div>
        <div className="crm-card-context">
          <span className="crm-card-context-label">Última conversa</span>
          <time>{date(lead.last_at ?? lead.updated_at)}</time>
        </div>
        <p className="crm-preview">
          {lead.last_text ||
            (lead.last_content_type === "image"
              ? "Imagem recebida"
              : lead.last_content_type === "audio"
                ? "Áudio recebido"
                : "Conversa iniciada")}
        </p>
        {lead.commercial_temperature && (
          <div className="crm-card-temperature">
            <TemperaturePill
              temperature={lead.commercial_temperature}
              source={lead.commercial_source}
            />
            {lead.commercial_reason && (
              <span title={lead.commercial_reason}>
                Leitura comercial disponível
              </span>
            )}
          </div>
        )}
        {(channelLabel || lead.tags.length > 0) && (
          <div className="crm-tags">
            {channelLabel && (
              <span className="crm-channel-tag">
                <i />
                {channelLabel}
              </span>
            )}
            {lead.tags.slice(0, 3).map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
        )}
        {(lead.assignee_name ||
          lead.next_action ||
          lead.value_cents !== null) && (
          <div className="crm-card-details">
            {lead.next_action && (
              <div className={`crm-next ${actionDue ? "is-due" : ""}`}>
                <span className="crm-detail-icon">
                  <PipelineIcon name="action" />
                </span>
                <span>
                  {lead.next_action}
                  {lead.next_action_at && (
                    <small>
                      {date(lead.next_action_at)}
                      {actionDue ? " · vencida" : ""}
                    </small>
                  )}
                </span>
              </div>
            )}
            {lead.assignee_name && (
              <div className="crm-owner">
                <span className="crm-detail-icon">
                  <PipelineIcon name="owner" />
                </span>
                <span>{lead.assignee_name}</span>
              </div>
            )}
            {lead.value_cents !== null && (
              <div className="crm-card-value">
                <span>Valor potencial</span>
                <strong>{money(lead.value_cents)}</strong>
              </div>
            )}
          </div>
        )}
        {lead.override_dimensions.length > 0 && (
          <small className="crm-human-control">Decisão humana</small>
        )}
        <div className="crm-card-actions">
          <button
            type="button"
            onClick={() => onOpenInbox(lead.conversation_id)}
          >
            <PipelineIcon name="message" /> Abrir conversa
          </button>
          <select
            aria-label={`Etapa de ${name}`}
            title={`Mover para ${displayStages.find((item) => item.stage_key === lead.stage)?.label ?? lead.stage_label}`}
            value={lead.stage}
            disabled={saving}
            onChange={(event) => requestMove(lead, event.target.value)}
          >
            {displayStages.map((item) => (
              <option key={item.id} value={item.stage_key}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
      </article>
    );
  }
  const stageCount = (key: Stage) =>
    leads.filter((lead) => lead.stage === key).length;
  const dueLeads = leads
    .filter(
      (lead) =>
        lead.next_action_at &&
        new Date(lead.next_action_at).getTime() <= Date.now() &&
        lead.stage !== "won" &&
        lead.stage !== "lost",
    )
    .sort((a, b) =>
      (a.next_action_at ?? "").localeCompare(b.next_action_at ?? ""),
    );
  const nextLeads = [
    ...dueLeads,
    ...leads
      .filter((lead) => !dueLeads.includes(lead))
      .sort(
        (a, b) =>
          new Date(b.last_at ?? b.updated_at).getTime() -
          new Date(a.last_at ?? a.updated_at).getTime(),
      ),
  ].slice(0, 4);
  const preparationIsInitial = Boolean(
    preparationState &&
      preparationState.activeVersion === 1 &&
      !preparationState.active.services.length &&
      !preparationState.active.commercialInfo &&
      !preparationState.active.businessHours &&
      !preparationState.active.businessObjectives &&
      !preparationState.active.humanHandoff &&
      !preparationState.active.faqs.length &&
      !preparationState.active.aiInstructions,
  );
  const qaClean = preparationState?.qaClean === true;
  const pendingOnboarding = preparationState?.onboarding === "draft" && !qaClean;
  const preparationLabel =
    pendingOnboarding
      ? "Em preparação"
      : preparationState?.hasDraft
        ? "Rascunho salvo"
        : preparationIsInitial
          ? "Informações iniciais"
          : preparationState
            ? "Informações aprovadas"
            : "Preparação da IA";
  const pendingPanel = pendingOnboarding ? (
    <section
      className="crm-preparation-continuity"
      aria-label="Informações em preparação"
    >
      <div className="crm-preparation-continuity-head">
        <span>EM PREPARAÇÃO</span>
        <h2>Continue de onde parou</h2>
        <p>
          Estas informações vieram do cadastro e ainda não orientam as respostas
          da IA.
        </p>
      </div>
      <dl>
        <div>
          <dt>Empresa</dt>
          <dd>
            {preparationState?.pending?.companyName ||
              tenantName ||
              "Ainda não informada"}
          </dd>
        </div>
        <div>
          <dt>Ramo de atividade</dt>
          <dd>
            {preparationState?.pending?.businessArea || "Ainda não informado"}
          </dd>
        </div>
        <div>
          <dt>Etapa</dt>
          <dd>
            {preparationState?.hasDraft
              ? "Rascunho salvo para continuar"
              : "Informações iniciais salvas"}
          </dd>
        </div>
      </dl>
      <div className="crm-preparation-continuity-foot">
        <p>
          <strong>Em uso pela IA:</strong>{" "}
          {preparationIsInitial
            ? "somente orientações predefinidas; o atendimento inicial ainda depende de aprovação."
            : "a configuração aprovada anteriormente, sem estas mudanças pendentes."}
        </p>
        <a href="/comecar">
          Retomar preparação <span aria-hidden="true">→</span>
        </a>
      </div>
    </section>
  ) : null;
  const activePanel =
    preparationState && !pendingOnboarding && !preparationIsInitial ? (
      <section
        className="crm-preparation-active"
        aria-label="Configuração aprovada e em uso"
      >
        <span>INFORMAÇÕES APROVADAS</span>
        <div>
          <strong>{preparationState.active.companyName || tenantName}</strong>
          <p>
            Ramo: {preparationState.active.businessArea || "Não informado"} ·{" "}
            {preparationState.active.services.length}{" "}
            {preparationState.active.services.length === 1
              ? "serviço informado"
              : "serviços informados"}
          </p>
        </div>
        <small>
          {preparationState.hasDraft
            ? "O rascunho salvo ainda não é usado pela IA. A configuração aprovada permanece como referência do Core."
            : "Esta é a configuração que o Core utiliza quando o atendimento automático está autorizado."}
        </small>
      </section>
    ) : null;
  const title =
    view === "dashboard"
      ? "Visão geral"
      : view === "pipeline"
        ? "Pipeline comercial"
        : view === "preparation"
          ? "Minha IA"
          : "Configurações";
  const description =
    view === "dashboard"
      ? "Seu atendimento e suas oportunidades, em uma única visão."
      : view === "pipeline"
        ? "Acompanhe cada oportunidade e organize o próximo passo."
        : view === "preparation"
          ? "Revise as informações que orientam o atendimento da sua empresa."
          : "Informações da sua operação, organizadas em um único lugar.";
  return (
    <section
      className={`crm-workspace ${view === "pipeline" ? "crm-pipeline-page" : ""}`}
      aria-label={title}
    >
      <header className="crm-header">
        <div>
          <p className="crm-overline">
            G3.AI <span>/</span> {tenantName || "Empresa"}
          </p>
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
        <div className="crm-header-actions">
          <ChannelOperationalStatus token={token} tenantId={tenantId} />
          <OperationalBell token={token} tenantId={tenantId} onOpenConversation={onOpenInbox} />
        </div>
      </header>
      {error && (
        <div className="crm-error" role="alert">
          {error}
        </div>
      )}
      {view==="dashboard"&&channel&&tenantRole==="owner"&&preparationState&&!pendingOnboarding&&!preparationIsInitial&&<section className="crm-preparation-continuity"><h2>{channel.status!=="connected"?"Preparação concluída, conexão pendente":!channel.ai_enabled?"Autorização da IA pendente":"Atendimento automático autorizado"}</h2><p>A preparação, a conexão e a autorização do atendimento são etapas independentes.</p>{<a href="/comecar?mode=login" onClick={()=>rememberTenant(tenantId)}>{channel.status!=="connected"?"Conectar meu WhatsApp":channel.ai_enabled?"Gerenciar conexão e atendimento":"Revisar autorização do atendimento"}</a>}</section>}
      {loading ? (
        <div className="crm-loading">Carregando dados da empresa…</div>
      ) : view === "preparation" ? (
        <div className="crm-preparation-settings">
          {tenantRole === "owner" ? (
            <>
              <section
                className={`crm-preparation-status ${preparationState?.onboarding === "draft" ? "pending" : preparationState?.hasDraft ? "draft" : preparationIsInitial ? "initial" : "approved"}`}
              >
                <span>MINHA IA</span>
                <div>
                  <strong>{preparationLabel}</strong>
                  <p>
                    {preparationState?.onboarding === "draft"
                      ? "Conclua as informações essenciais para preparar o WhatsApp desta empresa."
                      : preparationState?.hasDraft
                        ? "Há mudanças salvas aguardando sua revisão e aprovação."
                        : preparationIsInitial
                          ? "Esta empresa ainda usa apenas as orientações técnicas predefinidas. Complete as informações comerciais antes de ativar o atendimento."
                          : "Estas são as informações aprovadas que orientam o atendimento."}
                  </p>
                </div>
              </section>
              {activePanel}
              {pendingOnboarding ? (
                pendingPanel
              ) : (
                <PreparationP1Shell
                  key={tenantId}
                  token={token}
                  tenantId={tenantId}
                  mode="settings"
                  onApproved={(name,destination) => {
                    rememberTenant(tenantId);
                    window.location.assign(destination==="connect"?"/comecar?mode=login":"/app");
                    onTenantNameChange?.(name);
                    setPreparationEpoch((current) => current + 1);
                  }}
                />
              )}
            </>
          ) : (
            <section className="crm-settings-grid">
              <article className="crm-settings-hero">
                <div>
                  <span>PAINEL DA EMPRESA</span>
                  <h2>{tenantName || "Empresa"}</h2>
                  <p>
                    A edição das informações da IA está disponível ao
                    proprietário desta empresa.
                  </p>
                </div>
              </article>
            </section>
          )}
        </div>
      ) : view === "settings" ? (
        <div className="crm-preparation-settings">
          <section className="crm-settings-grid" aria-label="Conta e segurança">
            <article className="crm-settings-item crm-account-security">
              <span className="crm-settings-symbol security">✓</span>
              <div>
                <h2>Conta e segurança</h2>
                <p>
                  {googleAccount?.configured
                    ? googleAccount.linked
                      ? "Esta conta possui uma identidade Google vinculada. O acesso continua individual e não altera empresas, planos ou permissões."
                      : "Vincule uma conta Google depois de entrar com sua senha. A vinculação exige sua sessão atual e nunca é feita apenas por e-mail."
                    : "A entrada com Google ainda não está habilitada neste ambiente de staging."}
                </p>
                {googleAccount?.configured && !googleAccount.linked && (
                  <button
                    type="button"
                    className="crm-google-link"
                    disabled={googleLinkBusy}
                    onClick={() => void linkGoogle()}
                  >
                    {googleLinkBusy
                      ? "Abrindo Google…"
                      : "Vincular conta Google"}
                  </button>
                )}
              </div>
              <small>
                {googleAccount?.linked
                  ? "Google vinculado"
                  : googleAccount?.configured
                    ? "Opcional"
                    : "Indisponível"}
              </small>
            </article>
          </section>
        </div>
      ) : view === "dashboard" ? (
        <>
          <div className="crm-toolbar">
            <div className="crm-toolbar-label">
              DESEMPENHO{" "}
              <span>· últimos {Number.parseInt(period, 10)} dias</span>
            </div>
            <label>
              Período{" "}
              <select
                value={period}
                onChange={(event) =>
                  setPeriod(event.target.value as typeof period)
                }
              >
                <option value="7d">7 dias</option>
                <option value="30d">30 dias</option>
                <option value="90d">90 dias</option>
              </select>
            </label>
          </div>
          <section className="crm-executive">
            <div>
              <span className="crm-executive-kicker">CENTRAL DA OPERAÇÃO</span>
              <h2>
                {overview?.due
                  ? `${overview.due} ${overview.due === 1 ? "ação precisa" : "ações precisam"} da sua atenção`
                  : "Tudo pronto para o próximo atendimento"}
              </h2>
              <p>
                {overview?.due
                  ? "Veja os retornos vencidos e mantenha cada oportunidade em movimento."
                  : "Acompanhe as conversas, os resultados e o trabalho da sua equipe."}
              </p>
            </div>
            <div className="crm-executive-data">
              <span>OPORTUNIDADES</span>
              <strong>{leads.length}</strong>
              <small>no pipeline atual</small>
            </div>
          </section>
          <div className="crm-stat-grid">
            <div className="crm-stat featured">
              <span className="crm-stat-icon">
                <PipelineIcon name="lead" />
              </span>
              <span>NOVOS LEADS</span>
              <strong>{overview?.new_leads ?? 0}</strong>
              <small>Conversas iniciadas no período</small>
            </div>
            <div className="crm-stat">
              <span className="crm-stat-icon blue">
                <PipelineIcon name="active" />
              </span>
              <span>EM ATENDIMENTO</span>
              <strong>{overview?.active ?? 0}</strong>
              <small>Etapa atual do pipeline</small>
            </div>
            <div className="crm-stat">
              <span className="crm-stat-icon amber">
                <PipelineIcon name="waiting" />
              </span>
              <span>AGUARDANDO CLIENTE</span>
              <strong>{overview?.waiting ?? 0}</strong>
              <small>Retornos pendentes</small>
            </div>
            <div className="crm-stat">
              <span className="crm-stat-icon red">
                <PipelineIcon name="due" />
              </span>
              <span>PRÓXIMAS AÇÕES VENCIDAS</span>
              <strong>{overview?.due ?? 0}</strong>
              <small>Acompanhe as oportunidades</small>
            </div>
          </div>
          <div className="crm-dashboard-grid">
            <section className="crm-surface">
              <div className="crm-surface-head">
                <div>
                  <span>PIPELINE</span>
                  <h2>Onde estão as conversas</h2>
                </div>
                <span>{leads.length} oportunidades</span>
              </div>
              <div className="crm-stage-bars">
                {displayStages.map((item) => (
                  <div key={item.id}>
                    <span>{item.label}</span>
                    <div className="crm-bar">
                      <i
                        style={{
                          width: `${leads.length ? (stageCount(item.stage_key) / leads.length) * 100 : 0}%`,
                          background: stageAccent(item),
                        }}
                      />
                    </div>
                    <strong>{stageCount(item.stage_key)}</strong>
                  </div>
                ))}
              </div>
            </section>
            <section className="crm-surface crm-activity">
              <div className="crm-surface-head">
                <div>
                  <span>ATENDIMENTO</span>
                  <h2>Operação no período</h2>
                </div>
              </div>
              <div className="crm-activity-row">
                <span>Mensagens recebidas</span>
                <strong>{overview?.inbound ?? 0}</strong>
              </div>
              <div className="crm-activity-row">
                <span>Respostas automáticas</span>
                <strong>{overview?.ai ?? 0}</strong>
              </div>
              <div className="crm-activity-row">
                <span>Respostas humanas</span>
                <strong>{overview?.human ?? 0}</strong>
              </div>
              <div className="crm-activity-row">
                <span>WhatsApp</span>
                <ChannelOperationalStatus token={token} tenantId={tenantId} compact />
              </div>
              <div className="crm-activity-row">
                <span>Plano</span>
                <strong>{subscription?.planName ?? "Indisponível"}</strong>
              </div>
              {subscription?.usage && (
                <div className="crm-activity-row">
                  <span>Respostas disponíveis</span>
                  <strong>
                    {subscription.usage.remaining} / {subscription.usage.limit}
                  </strong>
                </div>
              )}
            </section>
          </div>
          <section className="crm-surface crm-recent">
            <div className="crm-surface-head">
              <div>
                <span>PRÓXIMOS PASSOS</span>
                <h2>Oportunidades para acompanhar</h2>
              </div>
              <span>{nextLeads.length} visíveis</span>
            </div>
            {nextLeads.map((lead) => (
              <button
                key={lead.id}
                type="button"
                onClick={() => onOpenInbox(lead.conversation_id)}
              >
                <span className="crm-avatar">
                  {(lead.display_name || lead.external_id)
                    .slice(0, 2)
                    .toUpperCase()}
                </span>
                <span className="crm-recent-copy">
                  <strong>{lead.display_name || lead.external_id}</strong>
                  <small>
                    {lead.next_action ||
                      displayStages.find(
                        (item) => item.stage_key === lead.stage,
                      )?.label}
                  </small>
                </span>
                <span
                  className={
                    dueLeads.includes(lead)
                      ? "crm-recent-due"
                      : "crm-recent-date"
                  }
                >
                  {dueLeads.includes(lead)
                    ? "Vencida"
                    : date(lead.last_at ?? lead.updated_at)}
                </span>
                <span className="crm-recent-arrow">↗</span>
              </button>
            ))}
            {leads.length === 0 && (
              <div className="crm-empty-state">
                <span>◇</span>
                <strong>O primeiro contato abre o caminho.</strong>
                <p>
                  Quando uma conversa chegar, ela aparecerá aqui e no Pipeline.
                </p>
              </div>
            )}
          </section>
          {tenantRole === "owner" &&
            (pendingOnboarding ||
              preparationState?.hasDraft ||
              preparationIsInitial) && (
              <section className="crm-preparation-callout">
                <div>
                  <span>MINHA IA</span>
                  <h2>
                    {pendingOnboarding
                      ? "Continue preparando sua IA"
                      : preparationState?.hasDraft
                        ? "Você tem informações para revisar"
                        : "Prepare as informações que guiam sua IA"}
                  </h2>
                  <p>
                    {qaClean
                      ? "Este ambiente de QA começou vazio. Abra Minha IA para conversar ou importar um site sem reutilizar informações de outro teste."
                      : pendingOnboarding
                      ? "Retome de onde parou. A conexão do WhatsApp continua bloqueada até a preparação inicial ser aprovada."
                      : preparationState?.hasDraft
                        ? "Revise seu rascunho antes de aplicá-lo ao atendimento."
                        : "A configuração atual ainda tem somente orientações predefinidas. Inclua serviços, horários e regras reais."}
                  </p>
                </div>
                <button type="button" onClick={onOpenPreparation}>
                  {pendingOnboarding
                    ? "Retomar preparação"
                    : "Abrir Minha IA"}
                </button>
              </section>
            )}
        </>
      ) : (
        <>
          <section
            className="crm-pipeline-overview"
            aria-label="Resumo da carteira"
          >
            <div className="crm-overview-label">
              <span>CARTEIRA COMERCIAL</span>
              <strong>Em um olhar</strong>
            </div>
            <div className="crm-overview-item">
              <span className="crm-overview-icon blue">
                <PipelineIcon name="opportunity" />
              </span>
              <div>
                <small>Oportunidades</small>
                <strong>{leads.length}</strong>
              </div>
            </div>
            <div className="crm-overview-item">
              <span className="crm-overview-icon purple">
                <PipelineIcon name="value" />
              </span>
              <div>
                <small>Valor em aberto</small>
                <strong>
                  {pricedOpenLeads.length ? money(openValue) : "Não informado"}
                </strong>
              </div>
            </div>
            <div className="crm-overview-item">
              <span className="crm-overview-icon green">
                <PipelineIcon name="calendar" />
              </span>
              <div>
                <small>Próximas ações</small>
                <strong>{upcomingActions}</strong>
              </div>
            </div>
            <div className="crm-overview-item">
              <span className="crm-overview-icon orange">
                <PipelineIcon name="alert" />
              </span>
              <div>
                <small>Ações vencidas</small>
                <strong>{overdueActions}</strong>
              </div>
            </div>
          </section>
          <div className="crm-board-heading">
            <div>
              <span>FLUXO COMERCIAL</span>
              <h2>Etapas do atendimento</h2>
              <p>
                Arraste oportunidades entre etapas ou use o seletor em cada
                card.
              </p>
            </div>
            <div className="crm-board-navigation">
              {tenantRole === "owner" && (
                <button
                  type="button"
                  className="crm-stage-settings-button"
                  onClick={() => {
                    setStageDraft(displayStages.map((stage) => ({ ...stage })));
                    setStageSettingsOpen(true);
                  }}
                >
                  Configurar etapas
                </button>
              )}
              <span>
                {Math.min(boardIndex + 1, visibleStages.length)} de{" "}
                {visibleStages.length} etapas
              </span>
              <button
                type="button"
                aria-label="Ver etapa anterior"
                disabled={boardEdges.start}
                onClick={() => scrollStage(Math.max(0, boardIndex - 1))}
              >
                <PipelineIcon name="chevronLeft" />
              </button>
              <button
                type="button"
                aria-label="Ver próxima etapa"
                disabled={boardEdges.end}
                onClick={() =>
                  scrollStage(
                    Math.min(visibleStages.length - 1, boardIndex + 1),
                  )
                }
              >
                <PipelineIcon name="chevronRight" />
              </button>
            </div>
          </div>
          <div className="crm-toolbar crm-pipeline-tools">
            <label className="crm-search">
              <PipelineIcon name="search" />
              <input
                aria-label="Buscar oportunidades"
                placeholder="Buscar contato, mensagem ou etiqueta"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
            <label>
              <span>Etapa</span>
              <select
                value={stageFilter}
                onChange={(event) => {
                  setStageFilter(event.target.value as Stage | "all");
                  setBoardIndex(0);
                  boardRef.current?.scrollTo({ left: 0 });
                }}
              >
                <option value="all">Todas as etapas</option>
                {displayStages.map((item) => (
                  <option key={item.id} value={item.stage_key}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Ordenar</span>
              <select
                value={sort}
                onChange={(event) => setSort(event.target.value as typeof sort)}
              >
                <option value="recent">Interação recente</option>
                <option value="action">Próxima ação</option>
                <option value="value">Maior valor</option>
              </select>
            </label>
            <span className="crm-result-count">
              {filtered.length} de {leads.length}
            </span>
          </div>
          <nav
            className="crm-stage-navigation"
            aria-label="Ir para etapa do pipeline"
          >
            {visibleStages.map((item, index) => (
              <button
                key={item.id}
                type="button"
                className={boardIndex === index ? "active" : ""}
                onClick={() => scrollStage(index)}
              >
                <i style={{ background: stageAccent(item) }} />
                {item.label}
                <span>
                  {
                    filtered.filter((lead) => lead.stage === item.stage_key)
                      .length
                  }
                </span>
              </button>
            ))}
          </nav>
          <div className="crm-board" ref={boardRef} onScroll={updateBoardIndex}>
            {visibleStages.map((item) => {
              const columnLeads = filtered.filter(
                (lead) => lead.stage === item.stage_key,
              );
              const valuedLeads = columnLeads.filter(
                (lead) => lead.value_cents !== null,
              );
              const stageTotal = valuedLeads.reduce(
                (total, lead) => total + (lead.value_cents ?? 0),
                0,
              );
              return (
                <div
                  className={`crm-column crm-column-${item.stage_key}`}
                  key={item.id}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    const lead = leads.find(
                      (entry) =>
                        entry.id === event.dataTransfer.getData("text/plain"),
                    );
                    if (lead && lead.stage !== item.stage_key)
                      requestMove(lead, item.stage_key);
                  }}
                >
                  <div className="crm-column-head">
                    <div>
                      <i style={{ background: stageAccent(item) }} />
                      <strong>{item.label}</strong>
                    </div>
                    <span>{columnLeads.length}</span>
                  </div>
                  {valuedLeads.length > 0 && (
                    <p className="crm-column-value">
                      {money(stageTotal)} em oportunidades com valor
                    </p>
                  )}
                  <div className="crm-column-body">
                    {columnLeads.map(card)}
                    {columnLeads.length === 0 && (
                      <p className="crm-column-empty">
                        Nenhuma oportunidade nesta etapa
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      {editing && (
        <div
          className="crm-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Editar oportunidade"
        >
          <form className="crm-modal" onSubmit={submitEdit}>
            <header>
              <div>
                <p className="crm-overline">OPORTUNIDADE</p>
                <h2>{editing.display_name || editing.external_id}</h2>
                <small>{editing.external_id}</small>
              </div>
              <button
                type="button"
                onClick={() => setEditing(null)}
                aria-label="Fechar"
              >
                ×
              </button>
            </header>
            {error && (
              <p className="crm-error" role="alert">
                {error}
              </p>
            )}
            <CommercialBrief
              insight={commercialInsight}
              loading={insightLoading}
              onRefresh={() => {
                const current = leads.find((lead) => lead.id === editing.id);
                if (current) openEdit(current);
              }}
            />
            <section
              className="crm-temperature-control"
              aria-label="Temperatura comercial"
            >
              <span>Temperatura comercial</span>
              <div>
                {(["cold", "warm", "hot"] as const).map((temperature) => (
                  <button
                    type="button"
                    key={temperature}
                    disabled={
                      saving || editing.commercial_temperature === temperature
                    }
                    className={
                      editing.commercial_temperature === temperature
                        ? `selected ${temperature}`
                        : temperature
                    }
                    onClick={() => void setTemperature(editing, temperature)}
                  >
                    {temperature === "cold"
                      ? "Frio"
                      : temperature === "warm"
                        ? "Morno"
                        : "Quente"}
                  </button>
                ))}
              </div>
              <small>
                A classificação manual é protegida até que alguém libere a
                automação.
              </small>
            </section>
            <div className="crm-form-grid">
              <label>
                Etapa
                <select
                  value={editing.stage}
                  onChange={(event) =>
                    setEditing({ ...editing, stage: event.target.value })
                  }
                >
                  {displayStages.map((item) => (
                    <option key={item.id} value={item.stage_key}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Responsável
                <select
                  value={editing.assigned_user_id ?? ""}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      assigned_user_id: event.target.value || null,
                    })
                  }
                >
                  <option value="">Sem responsável</option>
                  {operators.map((operator) => (
                    <option key={operator.id} value={operator.id}>
                      {operator.display_name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Próxima ação
                <input
                  value={editing.next_action}
                  onChange={(event) =>
                    setEditing({ ...editing, next_action: event.target.value })
                  }
                  placeholder="Ex.: Retornar proposta"
                  maxLength={240}
                />
              </label>
              <label>
                Quando
                <input
                  type="datetime-local"
                  value={localInputDate(editing.next_action_at)}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      next_action_at: event.target.value
                        ? new Date(event.target.value).toISOString()
                        : null,
                    })
                  }
                />
              </label>
              <label>
                Valor potencial (R$)
                <input
                  inputMode="decimal"
                  value={valueInput}
                  onChange={(event) => setValueInput(event.target.value)}
                  placeholder="Opcional"
                />
              </label>
              <label>
                Etiquetas
                <input
                  value={tagsInput}
                  onChange={(event) => setTagsInput(event.target.value)}
                  placeholder="Separe por vírgula"
                  maxLength={240}
                />
              </label>
              <label className="full">
                Notas internas
                <textarea
                  value={editing.internal_notes}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      internal_notes: event.target.value,
                    })
                  }
                  placeholder="Contexto para a equipe. Não é enviado ao cliente."
                  rows={5}
                />
              </label>
            </div>
            <section className="crm-history" aria-label="Histórico comercial">
              <h3>Histórico comercial</h3>
              {leadEvents.length ? (
                <ol>
                  {leadEvents.slice(0, 8).map((event) => (
                    <li key={event.id}>
                      <span>
                        {event.operation === "lead.temperature_changed"
                          ? "Temperatura atualizada"
                          : event.operation === "lead.stage_automated"
                            ? "Etapa atualizada pela automação"
                            : event.operation === "lead.stage_changed"
                              ? "Etapa atualizada"
                              : event.operation === "lead.commercial_analyzed"
                                ? "Leitura comercial atualizada"
                                : "Atualização comercial"}
                      </span>
                      <small>
                        {date(event.created_at)} ·{" "}
                        {event.source === "human"
                          ? event.actor_name || "equipe"
                          : event.source === "automation"
                            ? "automação"
                            : "sistema"}
                      </small>
                    </li>
                  ))}
                </ol>
              ) : (
                <p>Nenhum evento comercial registrado ainda.</p>
              )}
              <button
                type="button"
                className="crm-history-link"
                onClick={() => onOpenInbox(editing.conversation_id)}
              >
                Abrir conversa e evidências
              </button>
            </section>
            {editing.override_dimensions.length > 0 && (
              <aside className="crm-override-panel">
                <strong>Decisão humana em vigor</strong>
                <p>
                  Automação futura não poderá substituir estas dimensões até que
                  alguém da equipe as libere.
                </p>
                {editing.override_dimensions.includes("stage") && (
                  <button
                    type="button"
                    className="crm-secondary"
                    disabled={saving}
                    onClick={() => void releaseHumanControl(editing, "stage")}
                  >
                    Liberar automação da etapa
                  </button>
                )}
                {editing.override_dimensions.includes("assignment") && (
                  <button
                    type="button"
                    className="crm-secondary"
                    disabled={saving}
                    onClick={() =>
                      void releaseHumanControl(editing, "assignment")
                    }
                  >
                    Liberar automação do responsável
                  </button>
                )}
                {editing.override_dimensions.includes("temperature") && (
                  <button
                    type="button"
                    className="crm-secondary"
                    disabled={saving}
                    onClick={() =>
                      void releaseHumanControl(editing, "temperature")
                    }
                  >
                    Liberar automação da temperatura
                  </button>
                )}
              </aside>
            )}
            <footer>
              <button
                type="button"
                className="crm-secondary"
                onClick={() => onOpenInbox(editing.conversation_id)}
              >
                Abrir conversa
              </button>
              <button className="crm-primary" disabled={saving}>
                {saving ? "Salvando…" : "Salvar oportunidade"}
              </button>
            </footer>
          </form>
        </div>
      )}
      {stageSettingsOpen && (
        <div
          className="crm-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Configurar etapas"
        >
          <section className="crm-modal crm-stage-settings">
            <header>
              <div>
                <p className="crm-overline">PIPELINE</p>
                <h2>Configurar etapas</h2>
                <small>
                  As mudanças preservam os cartões e são registradas no
                  histórico.
                </small>
              </div>
              <button
                type="button"
                onClick={() => setStageSettingsOpen(false)}
                aria-label="Fechar"
              >
                ×
              </button>
            </header>
            <div className="crm-stage-settings-list">
              {stageDraft.map((stage, index) => (
                <div className="crm-stage-row" key={stage.id}>
                  <span className="crm-stage-order">{index + 1}</span>
                  <input
                    aria-label={`Nome da etapa ${index + 1}`}
                    value={stage.label}
                    maxLength={80}
                    onChange={(event) =>
                      setStageDraft((current) =>
                        current.map((item) =>
                          item.id === stage.id
                            ? { ...item, label: event.target.value }
                            : item,
                        ),
                      )
                    }
                  />
                  <small>
                    {stage.is_terminal
                      ? `${stage.terminal_outcome === "won" ? "Ganho" : "Perdido"} · decisão humana`
                      : stage.is_active
                        ? "Ativa"
                        : "Desativada"}
                  </small>
                  {!stage.is_terminal && (
                    <label className="crm-stage-active">
                      <input
                        type="checkbox"
                        checked={stage.is_active}
                        onChange={(event) =>
                          setStageDraft((current) =>
                            current.map((item) =>
                              item.id === stage.id
                                ? { ...item, is_active: event.target.checked }
                                : item,
                            ),
                          )
                        }
                      />{" "}
                      Ativa
                    </label>
                  )}
                  <div className="crm-stage-order-actions">
                    <button
                      type="button"
                      disabled={index === 0}
                      onClick={() =>
                        setStageDraft((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index - 1
                              ? current[index]!
                              : itemIndex === index
                                ? current[index - 1]!
                                : item,
                          ),
                        )
                      }
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      disabled={index === stageDraft.length - 1}
                      onClick={() =>
                        setStageDraft((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index + 1
                              ? current[index]!
                              : itemIndex === index
                                ? current[index + 1]!
                                : item,
                          ),
                        )
                      }
                    >
                      ↓
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <footer>
              <button
                type="button"
                className="crm-secondary"
                disabled={stageSettingsBusy || stageDraft.length >= 24}
                onClick={() => void addStage()}
              >
                Adicionar etapa
              </button>
              <span className="crm-stage-settings-hint">
                Ganho e Perdido permanecem protegidas.
              </span>
              <button
                type="button"
                className="crm-primary"
                disabled={
                  stageSettingsBusy ||
                  stageDraft.some((stage) => !stage.label.trim())
                }
                onClick={() => void saveStages()}
              >
                {stageSettingsBusy ? "Salvando…" : "Salvar etapas"}
              </button>
            </footer>
          </section>
        </div>
      )}
    </section>
  );
}
